import React, { useState, useEffect } from 'react';
import { Layers, Sparkles, ArrowRight, Palette, Wand2, Zap, Upload, CheckCircle, Image } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Asset types data outside component to avoid recreation on every render
const assetTypes = [
  { type: "Instagram Post", color: "from-pink-400 to-rose-500", delay: 800 },
  { type: "Facebook Ad", color: "from-blue-400 to-cyan-500", delay: 1200 },
  { type: "LinkedIn Banner", color: "from-indigo-400 to-purple-500", delay: 1600 },
  { type: "Email Header", color: "from-green-400 to-emerald-500", delay: 2000 }
];

const AssetAIAgent = () => {
  const [step, setStep] = useState(0);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (step === 0) {
        setStep(1); // Start processing
      } else if (step === 1) {
        setTimeout(() => setStep(2), 2000); // Show generating
      } else if (step === 2 && assets.length < assetTypes.length) {
        const nextAsset = assetTypes[assets.length];
        setTimeout(() => {
          setAssets(prev => [...prev, nextAsset]);
        }, nextAsset.delay);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [step, assets.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Auto Branded Assets
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch AI learn your brand and generate unlimited on-brand assets in seconds.
          </p>
        </div>

        {/* Animated Demo */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-20">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Upload Section */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-500 p-4 md:p-8">
              <div className="bg-white/10 backdrop-blur-sm border-2 border-dashed border-white/50 rounded-xl md:rounded-2xl p-6 md:p-8 text-center">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                  {step === 0 ? (
                    <Upload className="w-8 h-8 text-violet-500" />
                  ) : (
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  )}
                </div>
                <p className="text-white font-semibold mb-2">
                  {step === 0 ? "Upload Brand Guide" : "Brand Guide Uploaded ✓"}
                </p>
                <p className="text-violet-100 text-sm">
                  {step === 0 ? "Colors, fonts, logos..." : "AI learning your brand..."}
                </p>
              </div>
            </div>

            {/* Generation Progress */}
            <div className="p-4 md:p-8 bg-gradient-to-b from-gray-50 to-white">
              {step >= 1 && (
                <div className="mb-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Wand2 className="w-4 h-4 md:w-5 md:h-5 text-violet-500" />
                      <span className="font-semibold text-gray-900 text-sm md:text-base">Generating Assets...</span>
                    </div>
                    {assets.length === assetTypes.length && (
                      <span className="text-green-600 text-xs md:text-sm font-medium">Complete!</span>
                    )}
                  </div>
                  <div className="text-xs md:text-sm text-gray-600 mb-4">
                    {assets.length}/{assetTypes.length} assets created
                  </div>
                </div>
              )}

              {step >= 2 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                  {assets.map((asset, index) => (
                    <div key={index} className="animate-fadeIn">
                      <div className={`bg-gradient-to-br ${asset.color} rounded-2xl p-6 aspect-video flex items-center justify-center relative overflow-hidden group hover:scale-105 transition-transform duration-300`}>
                        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                        <div className="relative z-10 text-center">
                          <Image className="w-8 h-8 text-white mx-auto mb-2" />
                          <p className="text-white font-semibold text-sm">{asset.type}</p>
                        </div>
                        <div className="absolute top-2 right-2">
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-violet-400 to-purple-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Palette className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Brand Learning</h3>
            <p className="text-gray-600 leading-relaxed">
              AI learns your colors, fonts, and style automatically
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Wand2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Auto Content</h3>
            <p className="text-gray-600 leading-relaxed">
              Generate ads, posts, and visuals in seconds
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Bulk Variations</h3>
            <p className="text-gray-600 leading-relaxed">
              Create hundreds of personalized versions instantly
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-violet-400 to-purple-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <div className="text-4xl font-bold text-white">10x</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Faster production</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <Layers className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Perfect consistency</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <Zap className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Scale effortlessly</h3>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready to automate your content?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Start creating on-brand assets at scale
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AssetAIAgent;